import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppPath } from '@/hooks/useAppPath';

export interface IViewportSizeContextProps {
  height: number;
  width: number;
  reportScrollLock: (shouldLock: boolean) => void;
  scrollLocked: boolean;
}

export const ViewportSizeContext = createContext<IViewportSizeContextProps>({
  height: 0,
  width: 0,
} as IViewportSizeContextProps);

export const ViewportSizeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useAppPath();
  const pathnamePrev = useRef(pathname);
  const [viewport, setViewport] = useState<{ height: number; width: number }>({
    height: window?.visualViewport?.height || window?.innerHeight,
    width: window?.visualViewport?.width || window?.innerWidth,
  });

  const [scrollLocked, setScrollLocked] = useState(false);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        height: window?.visualViewport?.height || window?.innerHeight,
        width: window?.visualViewport?.width || window?.innerWidth,
      });
    };

    // Listen to visualViewport resize if supported
    if (window?.visualViewport) {
      window?.visualViewport.addEventListener('resize', updateViewport);
      window?.visualViewport.addEventListener('scroll', updateViewport); // optional
    } else {
      // Fallback: listen to window resize
      window?.addEventListener('resize', updateViewport);
    }

    // Also update on orientationchange (optional)
    window?.addEventListener('orientationchange', updateViewport);

    return () => {
      if (window?.visualViewport) {
        window?.visualViewport.removeEventListener('resize', updateViewport);
        window?.visualViewport.removeEventListener('scroll', updateViewport);
      } else {
        window?.removeEventListener('resize', updateViewport);
      }
      window?.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  const reportScrollLock = useCallback(
    (shouldLock: boolean) => {
      if (scrollLocked !== shouldLock) {
        setScrollLocked(shouldLock);
      }
    },
    [scrollLocked]
  );

  useEffect(() => {
    if (pathname !== pathnamePrev.current) {
      pathnamePrev.current = pathname;
      reportScrollLock(false);
    }
  }, [pathname, reportScrollLock]);

  const value = useMemo(
    () => ({
      height: viewport.height,
      width: viewport.width,
      scrollLocked,
      reportScrollLock,
    }),
    [reportScrollLock, scrollLocked, viewport.height, viewport.width]
  );

  return <ViewportSizeContext.Provider value={value}>{children}</ViewportSizeContext.Provider>;
};
